import { Box, BoxProps, SxProps, Theme, Typography } from '@mui/material';
import { PropsWithChildren, useMemo } from 'react';

import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';

export interface SidebarTabProps extends BoxProps {
  onClick: () => void;
  isActive: boolean;
  variant?: 'default' | 'compact';
}

/**
 * @description This is a presentational component that represents a large, horizontal tab in the sidebar.
 */
export function SidebarTab({
  isActive,
  variant = 'default',
  onClick,
  children,
  ...boxProps
}: PropsWithChildren<SidebarTabProps>) {
  const containerSx = useMemo(
    () => getSidebarTabStyles(isActive, variant),
    [isActive, variant]
  );
  const isCompact = variant === 'compact';
  const compactFontWeight = isActive
    ? FONT_WEIGHTS.medium
    : FONT_WEIGHTS.regular;

  return (
    <Box sx={containerSx}>
      {/* this element should be clickable and tabbable, but have no button styling or anything else */}
      <Box
        role="button"
        tabIndex={0}
        aria-current={isActive ? 'location' : undefined}
        onClick={onClick}
        {...boxProps}
      >
        <Typography
          variant={isCompact ? 'body-s' : 'title-xs'}
          sx={{
            py: isCompact ? 1 : 2,
            px: isCompact ? 1.5 : 2,
            color: COLORS.TEXT.PRIMARY,
            fontWeight: isCompact ? compactFontWeight : FONT_WEIGHTS.bold,
          }}
        >
          {children}
        </Typography>
      </Box>
    </Box>
  );
}
function getSidebarTabStyles(
  isActive: boolean,
  variant: SidebarTabProps['variant']
): SxProps<Theme> {
  if (variant === 'compact') {
    return {
      borderRadius: 1,
      cursor: 'pointer',
      background: isActive ? COLORS.GRAY[50] : undefined,
      '&:hover': { backgroundColor: COLORS.FUNCTIONAL.HOVER },
    };
  }

  const baseStyle = {
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: COLORS.GRAY[200],
    borderRight: '8px solid',
    borderRightColor: 'transparent',
    cursor: 'pointer',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': {
      backgroundColor: COLORS.FUNCTIONAL.HOVER,
    },
  };

  if (!isActive) return baseStyle;
  return Object.assign(baseStyle, {
    borderRightColor: COLORS.NAVY[600],
    background: COLORS.FUNCTIONAL.HOVER,
  });
}
